import { useState } from 'react';
import { cn } from '@/lib/utils';

interface APMInstance {
  assembly_order_id: string;
  order_number: string;
  passmark_rating: number;
  unit_count: number;
}

interface APMBadgeProps {
  passmarkRating: number | null;
  cpuMark?: number | null;
  graphics2dMark?: number | null;
  graphics3dMark?: number | null;
  memoryMark?: number | null;
  diskMark?: number | null;
  sampleCount?: number;
  instances?: APMInstance[];
  compact?: boolean;
}

// Score-based colour: smooth gradient from grey to amber as score increases
const MAX_SCORE = 20000;

function scoreColor(score: number): { text: string; bg: string; bar: string } {
  if (score < 5000) return { text: 'text-slate-600', bg: 'bg-slate-100', bar: 'bg-slate-400' };
  if (score < 10000) return { text: 'text-blue-600', bg: 'bg-blue-100', bar: 'bg-blue-500' };
  if (score < 15000) return { text: 'text-purple-600', bg: 'bg-purple-100', bar: 'bg-purple-500' };
  return { text: 'text-amber-500', bg: 'bg-amber-100', bar: 'bg-amber-500' };
}

export function APMBadge({ passmarkRating, cpuMark, graphics2dMark, graphics3dMark, memoryMark, diskMark, sampleCount, instances, compact }: APMBadgeProps) {
  const [instancesOpen, setInstancesOpen] = useState(false);

  if (passmarkRating == null) return null;

  const colors = scoreColor(passmarkRating);
  const progress = Math.min((passmarkRating / MAX_SCORE) * 100, 100);
  const hasInstances = instances && instances.length > 0;

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <span className={cn('text-xs font-bold tabular-nums', colors.text)}>
          {passmarkRating.toLocaleString()}
        </span>
        <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className={cn('h-full rounded-full', colors.bar)} style={{ width: `${progress}%` }} />
        </div>
      </div>
    );
  }

  const subScores = [
    { label: 'CPU', value: cpuMark },
    { label: '3D Graphics', value: graphics3dMark },
    { label: '2D Graphics', value: graphics2dMark },
    { label: 'Memory', value: memoryMark },
    { label: 'Disk', value: diskMark },
  ];
  const maxSub = Math.max(...subScores.map(s => s.value || 0), 1);

  return (
    <div className="space-y-3">
      {/* Main score */}
      <div>
        <div className="flex items-baseline gap-2">
          <span className={cn('text-3xl font-bold tabular-nums', colors.text)}>
            {passmarkRating.toLocaleString()}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          AOI Performance Metric
          {sampleCount != null && sampleCount > 0 && (
            <>
              <span> &middot; avg of {sampleCount} test{sampleCount !== 1 ? 's' : ''}</span>
              {hasInstances && (
                <span className="relative inline-block ml-1">
                  <button
                    onClick={() => setInstancesOpen(o => !o)}
                    className="text-blue-500 hover:text-blue-700 underline underline-offset-2 focus:outline-none"
                  >
                    {instancesOpen ? 'hide' : 'view orders'}
                  </button>
                  {instancesOpen && (
                    <div className="absolute left-0 top-5 z-10 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[220px]">
                      {instances!.map(inst => (
                        <a
                          key={inst.assembly_order_id}
                          href={`/stock-io/${inst.assembly_order_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center justify-between px-3 py-1.5 hover:bg-gray-50 gap-4 no-underline"
                          onClick={e => e.stopPropagation()}
                        >
                          <span className="text-xs text-gray-700 font-medium">
                            {inst.order_number}
                            {inst.unit_count > 1 && (
                              <span className="text-gray-400 font-normal ml-1">({inst.unit_count} units)</span>
                            )}
                          </span>
                          <span className={cn('text-xs font-bold tabular-nums', scoreColor(inst.passmark_rating).text)}>
                            {inst.passmark_rating.toLocaleString()}
                          </span>
                        </a>
                      ))}
                    </div>
                  )}
                </span>
              )}
            </>
          )}
        </p>
      </div>

      {/* Overall progress bar */}
      <div>
        <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
          <span>0</span>
          <span>{MAX_SCORE.toLocaleString()}+</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all', colors.bar)}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Sub-scores */}
      {subScores.some(s => s.value != null && s.value > 0) && (
        <div className="space-y-1.5 pt-1">
          {subScores.map(s => {
            if (s.value == null || s.value <= 0) return null;
            const pct = (s.value / maxSub) * 100;
            return (
              <div key={s.label} className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground w-20 shrink-0">{s.label}</span>
                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gray-400 rounded-full" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-[11px] font-mono tabular-nums w-14 text-right text-muted-foreground">{s.value.toLocaleString()}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
